import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  CardHeader,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Divider
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon, Visibility, VisibilityOff } from '@mui/icons-material';

interface UIConfig {
  showWelcomeMessage: boolean;
  showTypingIndicator: boolean;
  showSourceInfo: boolean;
  maxChatHistory: number;
  autoScrollChat: boolean;
}

const defaultUIConfig: UIConfig = {
  showWelcomeMessage: true,
  showTypingIndicator: true,
  showSourceInfo: true,
  maxChatHistory: 50,
  autoScrollChat: true
};

const UISettingsPage: React.FC = () => {
  const [config, setConfig] = useState<UIConfig>(defaultUIConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get the business config from the API
      const response = await fetch('https://us-central1-adriftchatbotv3.cloudfunctions.net/getConfig');
      if (!response.ok) {
        throw new Error('Failed to load configuration');
      }

      const businessConfig = await response.json();
      
      // Extract UI settings or use defaults
      if (businessConfig.ui) {
        setConfig({
          showWelcomeMessage: businessConfig.ui.showWelcomeMessage ?? true,
          showTypingIndicator: businessConfig.ui.showTypingIndicator ?? true,
          showSourceInfo: businessConfig.ui.showSourceInfo ?? true,
          maxChatHistory: businessConfig.ui.maxChatHistory ?? 50,
          autoScrollChat: businessConfig.ui.autoScrollChat ?? true
        });
      } else {
        setConfig(defaultUIConfig);
      }
    } catch (error: any) {
      console.error('Error loading UI config:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      setError(null);

      // Call the backend API to update UI settings
      const response = await fetch('https://us-central1-adriftchatbotv3.cloudfunctions.net/updateUISettings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'geoff@emertech.nz'
        },
        body: JSON.stringify({
          uiSettings: config
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save UI settings');
      }

      const result = await response.json();
      console.log('UI settings saved successfully:', result);
      
      setSuccess(true);
    } catch (error: any) {
      console.error('Error saving UI config:', error);
      setError(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleConfigChange = (field: keyof UIConfig, value: boolean | number) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCloseSnackbar = () => {
    setSuccess(false);
    setError(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          UI Settings
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadConfig}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveConfig}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Card>
        <CardHeader 
          title="Chat Interface Settings" 
          subheader="Control what users see in the chat interface"
        />
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            
            {/* Source Info Toggle */}
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.showSourceInfo}
                    onChange={(e) => handleConfigChange('showSourceInfo', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {config.showSourceInfo ? <Visibility sx={{ mr: 1, verticalAlign: 'middle' }} /> : <VisibilityOff sx={{ mr: 1, verticalAlign: 'middle' }} />}
                      Show Source Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Display RAG source information below AI responses. Shows users what documents/pages the AI used to generate answers.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', m: 0 }}
              />
            </Box>

            <Divider />

            {/* Other UI Settings */}
            <FormControlLabel
              control={
                <Switch
                  checked={config.showWelcomeMessage}
                  onChange={(e) => handleConfigChange('showWelcomeMessage', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Show Welcome Message</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Display a welcome message when users first open the chat
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={config.showTypingIndicator}
                  onChange={(e) => handleConfigChange('showTypingIndicator', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Show Typing Indicator</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Show animated typing indicator while AI is generating responses
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={config.autoScrollChat}
                  onChange={(e) => handleConfigChange('autoScrollChat', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Auto-scroll Chat</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Automatically scroll to the bottom when new messages arrive
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Current Configuration Display */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Current Configuration:</Typography>
            <Typography variant="body2" component="pre" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
              {JSON.stringify(config, null, 2)}
            </Typography>
          </Box>

          {/* Instructions */}
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Changes are applied immediately after saving. 
              The configuration will be updated in the business config file and affect all users instantly.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          UI settings updated successfully! Changes are now live.
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          Error: {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UISettingsPage; 